import {
  ref as rtdbRef,
  get as rtdbGet,
  onValue,
  update as rtdbUpdate,
} from 'firebase/database';
import { rtdb } from './firebase';

/**
 * Shared config for the AI search flows. Lives in the Realtime Database at
 * `configuration/ai` (all watchlock app-config is RTDB, not Firestore). The
 * backend Cloud Functions for both the image-recognition and text-search flows
 * read `maxResults` to decide how many top matches to return; this service
 * powers the admin form that sets it.
 */
const AI_SEARCH_PATH = '/configuration/ai';

/** How many results both AI search flows return when `maxResults` is unset. */
export const AI_MAX_RESULTS_DEFAULT = 3;

/** Allowed bounds for the shared AI search result count. */
export const AI_MAX_RESULTS_MIN = 1;
export const AI_MAX_RESULTS_MAX = 10;

export interface AiSearchConfig {
  /** Top matches each AI search returns (image rec + text). 1–10. */
  maxResults: number;
}

export const DEFAULT_AI_SEARCH_CONFIG: AiSearchConfig = {
  maxResults: AI_MAX_RESULTS_DEFAULT,
};

/**
 * Reads the config off the `ai` node. `maxResults` is an integer in
 * [AI_MAX_RESULTS_MIN, AI_MAX_RESULTS_MAX]; a missing/invalid value falls back
 * to the default so the backend's prior behaviour is preserved when unset.
 */
function normalise(value: unknown): AiSearchConfig {
  const node = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const raw = node.maxResults;
  const valid =
    typeof raw === 'number' &&
    Number.isInteger(raw) &&
    raw >= AI_MAX_RESULTS_MIN &&
    raw <= AI_MAX_RESULTS_MAX;
  return { maxResults: valid ? raw : AI_MAX_RESULTS_DEFAULT };
}

export async function getAiSearchConfig(): Promise<AiSearchConfig> {
  const snapshot = await rtdbGet(rtdbRef(rtdb, AI_SEARCH_PATH));
  return snapshot.exists() ? normalise(snapshot.val()) : DEFAULT_AI_SEARCH_CONFIG;
}

export function subscribeToAiSearchConfig(
  callback: (config: AiSearchConfig) => void,
): () => void {
  return onValue(rtdbRef(rtdb, AI_SEARCH_PATH), (snapshot) => {
    callback(snapshot.exists() ? normalise(snapshot.val()) : DEFAULT_AI_SEARCH_CONFIG);
  });
}

/**
 * Writes the shared AI search result count (clamped to the allowed bounds) to
 * RTDB. Uses `update` on the parent node so sibling keys under
 * `configuration/ai` are preserved.
 */
export async function setAiMaxResults(maxResults: number, userId?: string): Promise<void> {
  const clamped = Math.min(
    AI_MAX_RESULTS_MAX,
    Math.max(AI_MAX_RESULTS_MIN, Math.round(maxResults)),
  );
  await rtdbUpdate(rtdbRef(rtdb, AI_SEARCH_PATH), {
    maxResults: clamped,
    maxResultsUpdatedAt: Date.now(),
    ...(userId ? { maxResultsUpdatedBy: userId } : {}),
  });
}
